import { useCallback, useContext, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { t } from 'i18next'
import { isNull, isUndefined } from 'lodash'
import isNumber from 'lodash/isNumber'
import moment from 'moment'
import { IMessage } from '@stomp/stompjs'

import { PageContext as FormContext } from '@pages/FormPage'

import {
  getDefaultValuesByObjectFields,
  getExpandsParamForForm,
  mapFormKeysToCoreKeys,
  mergeFormDataAfterUpdate,
  shouldRefetchAfterUpdate,
} from '@components/DisplayForm/helpers'
import { useCreateObjectDataRecordWithFixedKey } from '@components/DisplayForm/hooks/useCreateObjectDataRecordWithFixedKey'
import { useFetchObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchObjectDataWithFixedKey'
import { useFetchPreFillObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchPreFillObjectDataWithFixedKey'
import { useUpdateObjectDataRecordWithFixedKey } from '@components/DisplayForm/hooks/useUpdateObjectDataRecordWithFixedKey'

import { useExecuteCommandMutation, useFetchObjectDataRecordEnrichedByIdMutation } from '@redux/api'
import { deleteFormField, setFormFields } from '@redux/reducers/changedFormFields.reducer'
import { selectIndexById } from '@redux/reducers/formHistory.reducer'

import { executeCommandAction, useAppDispatch, useAppSelector, useExecuteCommands, useOpenForm } from '@hooks'
import {
  executeCode,
  flattenFieldValues,
  getCommandRequestData,
  getDirtyValues,
  getLocalStorageContext,
  getReadObjectCommandConfig,
  getValuesFromVariables,
  isNotEmpty,
  isObjectValueType,
  prepareRequestParamsWithExpands,
  selectCommandForEntityAction,
  showSnackbarNotification,
} from '@helpers'
import {
  ACTION_CODE,
  ENTITY_COMMAND_TYPE,
  EVENT_CODE,
  EVENT_NAME_OBJECT_MESSAGE,
  FIELD_VALUE_TYPE,
  FORM_TYPE,
  OBJECT_FIELD_TYPE,
  OuterDataType,
} from '@constants'
import {
  ActionRow,
  FormRow,
  FormType,
  GETObjectModel,
  GETVariableDTO,
  LinkFormHistory,
  LinkType,
  LinkViewHistory,
  ObjectDataRecord,
  ObjectDataValue,
  ObjectFieldDTO,
  parseMessageBody,
  PassedFormData,
} from '@types'

import { processFieldChangeVariable } from '../../../hooks/processFieldChangeVariable'
import { mapCoreKeysToFormKeys } from '../helpers/mapCoreKeysToFormKeys'
import { PreFillConfig } from '../hooks/usePreFillConfig'
import { Row } from '../types'

const EMPTY_OUTER_VARIABLES: NonNullable<OuterDataType['variables']> = []

type useHandlersParams = {
  passedParams: PassedFormData
  formConfig: FormType
  pkField: ObjectFieldDTO | undefined
  // row: ObjectDataRecord | undefined
  fields: FormRow[]
  withPassedParams: boolean
  methods: UseFormReturn<Row>
  currentLink: LinkFormHistory
  dialogId?: string
  onBreadcrumbsMainLinkClick: (link?: LinkViewHistory) => void
  onObjectDataRecordSuccessCreating?: (links: LinkType[], data: ObjectDataRecord) => void
  handleRemoveSamePageForm: (formDataId?: number, isDirty?: boolean) => void
  handleUpdateSamePageFormData: (
    oldObjectId: string,
    newFormCode: string,
    objectCode: string,
    objectId: string
  ) => void
  handleCloseCurrentFormTab: () => void
  formCodeToFetchBy: string
  outerData?: OuterDataType
  resetFieldsAfterCreate?: boolean
  isIframeForm?: boolean
  preFillConfig: PreFillConfig
  instanceId: string
  latestVariables?: GETVariableDTO[]
  objectModel?: GETObjectModel
}

export const useHandlers = ({
  passedParams,
  formConfig,
  withPassedParams,
  methods,
  currentLink,
  dialogId,
  onBreadcrumbsMainLinkClick,
  onObjectDataRecordSuccessCreating,
  handleRemoveSamePageForm,
  handleUpdateSamePageFormData,
  formCodeToFetchBy,
  pkField,
  outerData,
  resetFieldsAfterCreate,
  isIframeForm,
  preFillConfig,
  instanceId,
  latestVariables = [],
  objectModel,
}: useHandlersParams) => {
  const { formCode, objectId, viewCode, viewId, path, globalId, typeId } = passedParams
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty, dirtyFields },
    setValue,
  } = methods
  const outerVariables = outerData?.variables ?? EMPTY_OUTER_VARIABLES

  const context = getLocalStorageContext()

  const {openForm} = useOpenForm()

  const formHistory = useAppSelector(state => state.formHistory)[globalId] || {}
  const { links, selectedIndex } = formHistory

  const [isPendingCreateOrUpdateForm, setPendingCreateOrUpdateForm] = useState(false)

  const [isViewEdit, setViewEdit] = useState(false)
  const [isDeleteRow, setIsDeleteRow] = useState(false)
  const [isDropdownWindowOpenFormState, setDropdownWindowOpenFormState] = useState(false)
  const [isSaveByEnterDisabled, setSaveByEnterDisabled] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isInitialValuesForCreateFormInitialized, setIsInitialValuesForCreateFormInitialized] =
    useState(currentLink.event !== FORM_TYPE.CREATE)

  const isViewType = currentLink.event === FORM_TYPE.VIEW && !isViewEdit
  // TODO Проверить, возможно нужно заменить, скорее всего нужно завязаться на formCode
  const [fetchEnrichedRow] = useFetchObjectDataRecordEnrichedByIdMutation()

  const [updateObjectDataRecord, { isSuccess: isSuccessUpdateObjectData }] =
    useUpdateObjectDataRecordWithFixedKey(globalId)

  const [createObjectDataRecord] = useCreateObjectDataRecordWithFixedKey(globalId)

  const [fetchForm, { data: row }] = useFetchObjectDataWithFixedKey(
    globalId,
    typeId,
    formConfig.fields
  )
  const [, { data: preFillFetchedRow }] = useFetchPreFillObjectDataWithFixedKey(globalId)

  const [executeCommand] = useExecuteCommandMutation()

  const { handleSetSearchParams } = useContext(FormContext)

  const { executeAllCommands } = useExecuteCommands()

  const dispatch = useAppDispatch()

  const handleSetHasUnsavedChanges = (value: boolean) => {
    setHasUnsavedChanges(value)
  }

  const handleSetSaveByEnterDisabled = (value: boolean) => {
    setSaveByEnterDisabled(value)
  }

  const handleDataForNewForm = ({ event, formCode, objectCode, objectId }: LinkFormHistory) => {
    // TODO: проверить нужно ли
    if (objectId && passedParams.objectId) {
      handleUpdateSamePageFormData(passedParams.objectId, formCode, objectCode, objectId)
    }
  }

  const handleObjectSubscribe = (msg: IMessage) => {
    const body = parseMessageBody(msg.body)
    const { objectCode, objectId: rowId, changedFields, eventName } = body
    const isCurrentRow = rowId === row?._id
    // TODO viewId здесь нет
    if (viewId && isCurrentRow) {
      switch (eventName) {
        case EVENT_NAME_OBJECT_MESSAGE.UPDATE:
          fetchEnrichedRow({
            objectCode,
            ids: [rowId],
            viewId,
            contextId: context?.id,
          }) // TODO Разобраться зачем тут вообще viewId
            .unwrap()
            .then(() => {
              if (changedFields) {
                dispatch(
                  setFormFields({
                    objectCode,
                    changedFields: Object.keys(changedFields),
                    eventName,
                    recordId: rowId,
                  })
                )

                setTimeout(() => {
                  dispatch(deleteFormField({ objectCode, recordId: rowId }))
                }, 3000)
              }
            })
          break
        case EVENT_NAME_OBJECT_MESSAGE.DELETE:
          setIsDeleteRow(true)
          break
      }
    }
  }

  const handleSetDropdownWindowOpenFormState = (value: boolean) => {
    return setDropdownWindowOpenFormState(value)
  }

  const handleSetType = (type: FORM_TYPE) => {
    if (formConfig?.objectCode && objectId && viewCode && path) {
      handleSetSearchParams({
        objectCode: formConfig.objectCode,
        objectId,
        viewCode,
        path,
        event: type,
      })
    }
  }

  const handleFormatValue = ([key, value]: [string, any]): [string, ObjectDataValue] => {
    const field = formConfig.fields?.find(field => field.code === key)

    if (!field) {
      return [key, value]
    }

    const isArray = field.type === OBJECT_FIELD_TYPE.ARRAY

    // Для поддержки обработки array-полей, добавили массовую обработку значений
    const originalValue = isArray ? value : [value]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resultValue: ObjectDataValue[] = originalValue.map((value: any) => {
      // Datetime
      if (value?._isAMomentObject && value?.isValid) {
        return moment(value).format()
      }

      // Linked object
      if (isObjectValueType(field.valueType)) {
        return value?.id ? value.id : value
      }

      // Enum
      if (field.type === OBJECT_FIELD_TYPE.ENUM) {
        return value
      }

      // Number
      if (
        field.valueType === FIELD_VALUE_TYPE.INTEGER ||
        field.valueType === FIELD_VALUE_TYPE.DOUBLE
      ) {
        if (isNumber(value)) {
          return value
        }

        return value ? Number(value) : null
      }

      // Default
      return value
    })

    return [key, isArray ? resultValue : resultValue[0]]
  }

  /**
   * @param link
   * @param alertHidden - для скрытия алерта подтверждения
   */
  const handleOpenLink = (newIndex: number, alertHidden?: boolean) => {
    const newLink = links[newIndex]

    const isAlert = !alertHidden && (isDirty || hasUnsavedChanges)
    if (isAlert && !confirm(t('notifications.leave'))) {
      return
    }

    if (dialogId) {
      dispatch(selectIndexById({ id: dialogId, index: newIndex }))
    }

    // вынес это из Breadcrumbs. Логика закрытия динамического и статического окна
    if (withPassedParams && !('formCode' in newLink)) {
      handleRemoveSamePageForm(passedParams.id)

      return
    }

    // Вынес наружу в onBreadcrumbsMainLinkClick
    if (newIndex === 0 && newLink.isRootLink) {
      onBreadcrumbsMainLinkClick()

      return
    }
    handleResetEdit()

    handleDataForNewForm(newLink as LinkFormHistory)
  }

  const handleResetFormWithNewRowData = useCallback(
    (data: { row: ObjectDataRecord }) => {
      const { row } = data

      const currentFormValues = getValues()
      const mergedData = mergeFormDataAfterUpdate(currentFormValues, row, formConfig.fields || [])

      reset(mergedData)
    },
    [getValues, reset, formConfig.fields]
  )

  const handleSave = handleSubmit((data: Row) => {
    if (isPendingCreateOrUpdateForm) {
      return
    }

    const formId = formConfig?.id

    if (!formId) {
      return
    }

    // Убрал проверку, мешало сохранять виртуальную форму, у которой нет Primary Key
    // if (!pkField) {
    // return
    // }

    const rowData: ObjectDataRecord = Object.fromEntries(
      Object.entries(data).map(([key, value]) => handleFormatValue([key, value]))
    )

    const dirtyRowData = getDirtyValues(dirtyFields, rowData)

    const hasFile = Object.values(dirtyRowData).some(value => value instanceof File)

    const fields = formConfig.fields || []

    const transformedVariables = getValuesFromVariables(outerVariables)
    const mappedRowData = mapFormKeysToCoreKeys(rowData, fields)

    const srcObj = {
      outerVariables: transformedVariables,
      ...mappedRowData,
    }

    const mappedDirtyRowData = mapFormKeysToCoreKeys(dirtyRowData, fields)

    const createCommand = selectCommandForEntityAction({
      overrideCommands: formConfig.commands,
      objectCommands: objectModel?.commands,
      actionType: ENTITY_COMMAND_TYPE.CREATE,
    })

    const updateCommand = selectCommandForEntityAction({
      overrideCommands: formConfig.commands,
      objectCommands: objectModel?.commands,
      actionType: ENTITY_COMMAND_TYPE.UPDATE,
    })

    const isEditMode = (currentLink.event === FORM_TYPE.EDIT || isViewEdit) && row

    if (outerData?.commands?.length) {
      executeAllCommands(outerData?.commands, outerData?.srcObj, dirtyRowData).then(() => {
        onObjectDataRecordSuccessCreating?.(links, {})
        setPendingCreateOrUpdateForm(false)
      })

      return
    }

    if (isEditMode) {
      // Редактирование записи
      if (!updateCommand) {
        showSnackbarNotification({
          text: t('notifications.updateCommandMissing'),
          options: { variant: 'error' },
        })

        return
      }

      if (!pkField || !row) return

      if (Object.keys(mappedDirtyRowData).length === 0) return

      setPendingCreateOrUpdateForm(true)

      const computedUpdateParams = updateCommand.parameters?.length
        ? getCommandRequestData(updateCommand.parameters, srcObj)
        : undefined

      const computedUpdateBody = updateCommand.body?.length
        ? getCommandRequestData(updateCommand.body, srcObj)
        : undefined

      const updateData = { ...mappedDirtyRowData }
      updateData[pkField.name] = row[pkField.name] // Достаём primary key, чтобы отправить его в запрос

      // Определяем, нужно ли выполнять GET после PUT
      // GET нужен только если изменялись поля типа OBJ_INTERNAL_ID_LINK или OBJ_PK_LINK,
      // так как эти поля требуют обогащения (получения label значений)

      updateObjectDataRecord({
        method: updateCommand.httpMethod,
        objectCode: updateCommand.objectCode || formConfig.objectCode,
        commandCode: updateCommand.commandCode,
        record: updateData,
        isFormData: hasFile,
        resource: updateCommand.resource,
        params: computedUpdateParams,
        body: computedUpdateBody,
      })
        .unwrap()
        .then(response => {
          // Выполняем GET только если были изменены поля, требующие обогащения
          const needsRefetch = shouldRefetchAfterUpdate(
            updateData,
            formConfig.fields || [],
            response
          )

          if (needsRefetch) {
            const objectIdToRefetch = row[pkField.name]
            if (!objectIdToRefetch) {
              handleResetFormWithNewRowData({ row: response })

              return response
            }

            const readCommandConfig = getReadObjectCommandConfig({
              objectId: String(objectIdToRefetch),
              typeId,
              overrideCommands: formConfig.commands,
              objectCommands: objectModel?.commands,
            })

            if (!readCommandConfig) {
              handleResetFormWithNewRowData({ row: response })

              return response
            }

            const { command: readCommand, sourceParams, defaultParams } = readCommandConfig

            const computedParams = readCommand.parameters?.length
              ? getCommandRequestData(readCommand.parameters, sourceParams)
              : readCommand.isOverridden
              ? {}
              : defaultParams

            const computedBody = readCommand.body?.length
              ? getCommandRequestData(readCommand.body, sourceParams)
              : undefined

            const formExpands = getExpandsParamForForm(formConfig.fields || [])
            const { requestParams, expands } = prepareRequestParamsWithExpands(
              computedParams,
              formExpands
            )

            return fetchForm({
              method: readCommand.httpMethod,
              objectCode: readCommand.objectCode || formConfig.objectCode,
              commandCode: readCommand.commandCode,
              resource: readCommand.resource,
              params: requestParams,
              body: computedBody,
              expands,
            })
              .unwrap()
              .then(newRowData => {
                const rowData = Array.isArray(newRowData) ? newRowData[0] : newRowData
                const mappedRowData = mapCoreKeysToFormKeys(rowData, formConfig.fields || [])

                const newFormData = getDefaultValuesByObjectFields(
                  formConfig?.fields || [],
                  mappedRowData
                )

                reset(newFormData)

                handleResetFormWithNewRowData({ row: mappedRowData })

                return newRowData
              })
          }

          // Если GET не требуется, обновляем форму вручную
          // Объединяем текущие данные формы с ответом PUT, сохраняя обогащенные поля (label)
          handleResetFormWithNewRowData({ row: response })

          return Promise.resolve(response)
        })
        .catch(() => {
          reset()
        })
        .finally(() => {
          setViewEdit(false)
          setPendingCreateOrUpdateForm(false)
        })
    } else {
      if (!createCommand) {
        showSnackbarNotification({
          text: t('notifications.createCommandMissing'),
          options: { variant: 'error' },
        })

        return
      }

      setPendingCreateOrUpdateForm(true)

      // Создание записи
      const createData = { ...mappedDirtyRowData }
      const computedCreateParams = createCommand.parameters?.length
        ? getCommandRequestData(createCommand.parameters, srcObj)
        : undefined

      const computedCreateBody = createCommand.body?.length
        ? getCommandRequestData(createCommand.body, srcObj)
        : undefined

      createObjectDataRecord({
        method: createCommand.httpMethod,
        objectCode: createCommand.objectCode || formConfig.objectCode,
        commandCode: createCommand.commandCode,
        resource: createCommand.resource,
        record: createData,
        isFormData: hasFile,
        params: computedCreateParams,
        body: computedCreateBody,
      })
        .unwrap()
        .then(data => {
          if (data) {
            if (selectedIndex > 0) {
              handleOpenLink(selectedIndex - 1, true)

              return
            }
            onObjectDataRecordSuccessCreating?.(links, data)
            // navigate('/' + (links[0].path || viewPath))
            if (isIframeForm) {
              window.parent.postMessage(
                {
                  type: 'FORM_SAVED',
                  payload: {
                    formCode,
                    objectId: data._id,
                    success: true,
                    action: 'create',
                  },
                },
                '*'
              )
            }
          }
        })
        .finally(() => {
          setPendingCreateOrUpdateForm(false)

          if (resetFieldsAfterCreate) {
            reset()

            setTimeout(() => handleSetInitialFieldsValuesForCreateForm(), 0)
          }
        })
    }
  })

  const handleSetFieldValuesOnFormInit = () => {
    const formFieldsCodes = formConfig.fields?.map(field => field.code)

    const formValues = getValues()
    const transformedVariables = getValuesFromVariables(outerVariables)

    // srcObj — только outerVariables для обратной совместимости; остальное — верхнеуровневые параметры
    const srcObj = { outerVariables: transformedVariables }

    const context = {
      srcObj,
      rowData: row ?? {},
      currentRowValues: flattenFieldValues(formValues),
      formValues,
      formVariables: latestVariables,
      outerVariables: transformedVariables,
      enrichedObject: row ?? {},
    }

    const handleSetValueAction = (action: ActionRow, commandResponse?: ObjectDataRecord) => {
      if (!action.jsValue) return

      const initValueField = executeCode(action.jsValue, {
        ...context,
        commandResponse,
      })

      if (initValueField === false || isUndefined(initValueField) || isNull(initValueField)) return

      setValue(action.objectFieldCode, initValueField as never, {
        shouldDirty: true,
        shouldTouch: true,
      })

      // setValue не генерирует type:'change' в watch, поэтому
      // useChangeVariableWithAction не узнает об изменении — вызываем вручную
      const updatedFormValues = getValues()
      processFieldChangeVariable(updatedFormValues, action.objectFieldCode, {
        row: row ?? updatedFormValues,
        formVariables: latestVariables,
        outerVariables,
        actions: formConfig?.actions ?? [],
        uniqueEventId: instanceId,
        executeCommand,
        openForm,
      })
    }

    formConfig?.actions?.forEach(action => {
      if (
        action.actionCode === ACTION_CODE.SET_FIELD_VALUE &&
        formFieldsCodes?.includes(action.objectFieldCode) &&
        action.jsValue &&
        action.eventCode === EVENT_CODE.ON_INIT_FORM
      ) {
        if (action.command) {
          executeCommandAction({
            action,
            executeCommand,
            context,
          }).then(commandResponse => {
            handleSetValueAction(action, commandResponse)
          })

          return
        }

        handleSetValueAction(action)
      }
    })
  }

  const handleSetFieldsValuesWithPrefill = () => {
    const values = preFillFetchedRow
      ? getDefaultValuesByObjectFields(formConfig.fields || [], preFillFetchedRow)
      : {}

    // Предзаполняем поля и делаем их dirty
    Object.entries(values).forEach(([key, value]) => {
      if (isNotEmpty(value)) {
        setValue(key, value, { shouldDirty: true, shouldTouch: true })

        // setValue не генерирует type:'change' в watch, поэтому
        // useChangeVariableWithAction не узнает об изменении — вызываем вручную
        const updatedFormValues = getValues()
        processFieldChangeVariable(updatedFormValues, key, {
          row: row ?? updatedFormValues,
          formVariables: latestVariables,
          outerVariables,
          actions: formConfig?.actions ?? [],
          uniqueEventId: instanceId,
          executeCommand,
          openForm,
        })
      }
    })
  }

  const handleSetInitialFieldsValuesForCreateForm = () => {
    if (currentLink.event === FORM_TYPE.CREATE) {
      // Прововеряем есть ли статический или динамический Pre Fill
      const hasPreFill = !isUndefined(preFillConfig.preFill)
      if (hasPreFill) handleSetFieldsValuesWithPrefill()

      handleSetFieldValuesOnFormInit()

      setIsInitialValuesForCreateFormInitialized(true)
    }
  }

  const handleCancel = () => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    const defaultValues = getDefaultValuesByObjectFields(formConfig?.fields || [], row || {})

    // TODO добавить сброс установленных переменных

    isDirty && reset(defaultValues)
    isDirty && setTimeout(() => handleSetInitialFieldsValuesForCreateForm(), 0)

    setViewEdit(false)
  }

  const handleEditOrSaveClick = () => {
    if (isViewType) {
      setViewEdit(true)

      return
    }

    if (isDirty) {
      handleSave()
    }
  }

  const handleResetEdit = () => setViewEdit(false)

  return {
    data: {
      isViewType,
      links,
      row,
      preFillFetchedRow,
    },
    state: {
      isViewEdit,
      isDeleteRow,
      isDropdownWindowOpenFormState,
      isSaveByEnterDisabled,
      isSuccessUpdateObjectData,
      isPendingCreateOrUpdateForm,
      hasUnsavedChanges,
      isInitialValuesForCreateFormInitialized,
    },
    handlers: {
      handleDataForNewForm,
      handleObjectSubscribe,
      handleSetType,
      handleFormatValue,
      handleOpenLink,
      handleSave,
      handleCancel,
      handleEditOrSaveClick,
      handleResetEdit,
      handleSetDropdownWindowOpenFormState,
      handleSetSaveByEnterDisabled,
      handleSetHasUnsavedChanges,
      handleSetFieldsValuesWithPrefill,
      handleSetFieldValuesOnFormInit,
      handleSetInitialFieldsValuesForCreateForm,
      handleResetFormWithNewRowData,
    },
  }
}
